import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/ca-activities.js

/**
 * ActivitiesHandler
 * 
 * Manages all activity-related operations for carbon accounting:
 * - API interaction for CRUD operations
 * - Data transformation between API and UI formats
 * - Real-time updates via WebSocket
 * - Activity scope determination and validation
 * - Integration with emissions recalculation workflow
 */
export class ActivitiesHandler {
  /**
   * Initialize the activities handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;

    // Register with WebSocketHandler if available
    if (this.store.webSocketHandler) {
      this.store.webSocketHandler.registerHandler(
        'ActivitiesHandler',
        this.handleUpdate.bind(this),
        [
          'activity',
          'activity:update',
          'activity:create',
          'activity:delete'
        ],
        this
      );
    }
  }

  /**
   * Load all activity data from API and transform into year-based structure
   * @returns {Promise<Object>} Activity data grouped by year
   */
  async loadActivityData() {
    try {
      this.store.state.loading.activityData = true;
      this.store.notifySubscribers('loading');
      const projectId = this.store.getProjectId();

      // Updated URL with projectId
      const url = `${process.env.GATSBY_STRAPI_URL}/api/ca-activities-data/project/${projectId}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch activity data: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const { data: rawData } = await response.json();
      const byYear = this.processRawActivityData(rawData);

      // Update store with simplified metadata
      this.store.state.data.activityData = {
        byYear,
        meta: {
          lastSync: new Date().toISOString()
        }
      };

      this.store.notifySubscribers('activityData');
      return byYear;

    } catch (error) {
      console.error('[Activities Handler] Error loading activity data:', error);
      this.store.handleError('activityData', error);
      return {};
    } finally {
      this.store.state.loading.activityData = false;
      this.store.notifySubscribers('loading');
    }
  }

  /**
   * Add a new activity
   * @param {string} year - Year for the activity
   * @param {string} scope - Scope of the activity (scope1, scope2, scope3)
   * @param {Object} activity - Activity data to add
   * @returns {Promise<Object>} Created activity data
   * @throws {Error} If validation fails or API request fails
   */
  async addActivity(year, scope, activity) {
    try {
      this.store.state.loading.activityData = true;
      const projectId = this.store.getProjectId();

      if (!projectId) {
        throw new Error('Project ID is required to add activity');
      }

      // Validate required fields
      if (!activity.activityId) {
        throw new Error('Activity ID is required');
      }

      if (!year) {
        throw new Error('Year is required');
      }

      // Validate numeric fields
      const amount = parseFloat(activity.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Amount must be a positive number');
      }

      // Prepare request data with proper type conversions
      const requestData = {
        projectId: parseInt(projectId),
        Year: year.toString(),
        ActivityId: activity.activityId.toString(),
        Amount: amount.toString(),
        CountryCode: activity.countryCode || '',
        ResourceId: activity.id ? activity.id.toString() : Math.floor(Math.random() * 1000000).toString(),
        ActivityDataId: crypto.randomUUID()
      };

      // First create in Strapi
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-activities-data/project/${projectId}/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: requestData
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to add activity: ${response.statusText}`);
      }

      const result = await response.json();

      // Then sync to Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-activities-data/project/${projectId}/${result.data.id}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Activity created but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // Set recalculation flag for this year
      await this.store.emissionsHandler.flagYearForRecalculation(year);

      // Notify emissions handler about the creation
      this.store.emissionsHandler.handleUpdate({
        action: 'create',
        method: 'addActivity',
        year: year,
        scope: scope,
        timestamp: new Date().toISOString(),
        source: 'direct',
        details: {
          id: result.data.id,
          activityId: activity.activityId
        }
      });

      // Reload data
      await this.loadActivityData();
      return result;

    } catch (error) {
      console.error('Failed to add activity:', error);
      this.store.handleError('activityData', error);
      throw error;
    } finally {
      this.store.state.loading.activityData = false;
    }
  }

  /**
   * Update an existing activity
   * @param {string} year - Year of the activity
   * @param {string} scope - Scope of the activity (scope1, scope2, scope3)
   * @param {string} documentId - Document ID to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated activity data
   * @throws {Error} If validation fails or API request fails
   */
  async updateActivity(year, scope, documentId, updates) {
    try {
      this.store.state.loading.activityData = true;
      const projectId = this.store.getProjectId();

      if (!projectId) {
        throw new Error('Project ID is required to update activity');
      }

      if (!documentId) {
        throw new Error('Document ID is required to update activity');
      }

      // Validate numeric fields if present
      if (updates.amount !== undefined) {
        const amount = parseFloat(updates.amount);
        if (isNaN(amount) || amount <= 0) {
          throw new Error('Amount must be a positive number');
        }
        // Ensure amount is a string for consistency
        updates.amount = amount.toString();
      }

      // 1. Update in Strapi
      const updateResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-activities-data/project/${projectId}/${documentId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: {
              Amount: updates.amount
            }
          })
        }
      );

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        throw new Error(`Update failed: ${errorData.error?.message || updateResponse.statusText}`);
      }

      const updatedData = await updateResponse.json();

      // 2. Sync updated activity to Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-activities-data/project/${projectId}/${documentId}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Activity updated but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // 3. Flag for recalculation
      await this.store.emissionsHandler.flagYearForRecalculation(year);

      // Notify emissions handler about the update
      this.store.emissionsHandler.handleUpdate({
        action: 'update',
        method: 'updateActivity',
        year: year,
        scope: scope,
        id: documentId,
        timestamp: new Date().toISOString(),
        source: 'direct',
        details: {
          updates: updates
        }
      });

      // Update local state
      await this.loadActivityData();

      return updatedData;
    } catch (error) {
      console.error('Activity update failed:', error);
      this.store.handleError('activityData', error);
      throw error;
    } finally {
      this.store.state.loading.activityData = false;
    }
  }

  /**
   * Delete an activity
   * @param {string} year - Year of the activity
   * @param {string} scope - Scope of the activity
   * @param {string} documentId - Document ID to delete
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async deleteActivity(year, scope, documentId) {
    try {
      this.store.state.loading.activityData = true;
      const projectId = this.store.getProjectId();

      if (!projectId) {
        throw new Error('Project ID is required to delete activity');
      }

      if (!documentId) {
        throw new Error('Document ID is required to delete activity');
      }

      // Store activity details before deletion
      let activityDetails = null;
      if (this.store.state.data.activityData?.byYear?.[year]?.[scope]) {
        activityDetails = this.store.state.data.activityData.byYear[year][scope].find(a => a.id === documentId);
      }

      // 1. Delete from Strapi (the backend controller already handles Snowflake sync)
      const deleteUrl = `${process.env.GATSBY_STRAPI_URL}/api/ca-activities-data/project/${projectId}/${documentId}`;
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete activity: ${response.status} ${response.statusText} - ${errorText}`);
      }

      // 2. REMOVED the cleanup sync - not needed for individual deletions
      // The backend should handle individual deletions properly without bulk cleanup

      // 3. Set recalculation flag for this year
      await this.store.emissionsHandler.flagYearForRecalculation(year);

      // 4. Reload data to update UI
      await this.loadActivityData();

      // 5. Explicitly notify the emissions handler about the deletion
      this.store.emissionsHandler.handleUpdate({
        action: 'delete',
        method: 'delete',
        year: year,
        scope: scope,
        id: documentId,
        timestamp: new Date().toISOString(),
        source: 'direct',
        activityDeleted: true,
        details: activityDetails
      });

      return true;

    } catch (error) {
      console.error('Failed to delete activity:', error);
      this.store.handleError('activityData', error);
      throw error;
    } finally {
      this.store.state.loading.activityData = false;
    }
  }

  /**
   * Get activity data for specific year
   * @param {string|number} year - Year to get data for
   * @returns {Object|null} Activity data by scope or null if not loaded
   */
  getActivityData(year) {
    return this.store.state.data.activityData.byYear[year] || null;
  }

  /**
   * Check if activity data exists for year
   * @param {string|number} year - Year to check
   * @returns {boolean} True if data exists
   */
  hasActivityData(year) {
    return !!this.store.state.data.activityData.byYear[year];
  }

  /**
   * Get all available years with activity data
   * @returns {Array<string>} Array of years sorted in descending order
   */
  getAvailableYears() {
    return Object.keys(this.store.state.data.activityData.byYear)
      .sort((a, b) => b.localeCompare(a));
  }

  /**
   * Process raw activity data into year-based structure
   * @private
   * @param {Array} rawData - Raw data from API
   * @returns {Object} Processed data grouped by year and scope
   */
  processRawActivityData(rawData) {
    if (!Array.isArray(rawData)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Invalid activity data received:', rawData);
      }
      return {};
    }

    const byYear = {};

    rawData.forEach(item => {
      const data = item.attributes || item;
      const year = data?.Year?.toString();
      const activityId = data?.ActivityId;

      if (!year || !activityId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Skipping invalid activity data item:', item);
        }
        return;
      }

      const scope = this.determineScope(activityId);
      if (!scope) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Could not determine scope for activity ID: ${activityId}`);
        }
        return;
      }

      if (!byYear[year]) {
        byYear[year] = {
          scope1: [],
          scope2: [],
          scope3: []
        };
      }

      byYear[year][scope].push(this.transformActivityData(item));
    });

    return byYear;
  }

  /**
   * Transform individual activity data entry
   * @private
   * @param {Object} item - Raw activity data item
   * @returns {Object} Transformed activity data
   */
  transformActivityData(item) {
    if (!item) return null;

    const data = item.attributes || item;

    return {
      id: item.id,
      instanceId: data.ActivityDataId,
      year: data.Year,
      scope: this.determineScope(data.ActivityId),
      activityId: data.ActivityId,
      amount: parseFloat(data.Amount || '0'),
      unit: data.Unit || '',
      snowflakeId: data.SnowflakeId,
      resourceId: data.ResourceId,
      countryCode: data.CountryCode || '',
      createdByUser: data.CreatedByUser,
      lastUpdatedByUser: data.LastUpdatedByUser,
      lastUpdatedOn: data.SnowflakeLastUpdatedOn || data.updatedAt,
      emissionFactor: parseFloat(data.EmissionFactorExactValue || '0.2')
    };
  }

  /**
   * Determine scope from activity ID
   * @private
   * @param {string} activityId - Activity ID to check
   * @returns {string|null} Scope (scope1, scope2, scope3) or null if not found
   */
  determineScope(activityId) {
    if (!activityId) return null;

    const activities = this.store.state.data.activities;
    if (!Array.isArray(activities)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Activities list not loaded yet');
      }
      return null;
    }

    const activity = activities.find(a => a.activityId === activityId);

    if (!activity) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Activity not found for scope determination:', activityId);
      }
      return null;
    }

    const sheet = activity.dataCollectionSheet?.toUpperCase();
    if (sheet === 'SCOPE1') return 'scope1';
    if (sheet === 'SCOPE2') return 'scope2';
    if (sheet === 'SCOPE3') return 'scope3';

    return null;
  }

  /**
   * Handle real-time activity data updates from WebSocket
   * @param {Object} data - Update data from WebSocket
   * @returns {Promise<void>}
   */
  async handleUpdate(data) {
    // Extract year from multiple possible locations in the data structure
    const year = data.year || data.Year ||
      (data.data && (data.data.Year || data.data.year)) || null;

    if (!year) {
      console.error('No year in activity update data:', data);
      return;
    }

    try {
      // Simply reload the data
      await this.loadActivityData();

      // Explicitly notify subscribers of the updated data
      this.store.notifySubscribers('activityData', this.store.state.data.activityData);

      // Flag the year for recalculation after reload
      await this.store.emissionsHandler.flagYearForRecalculation(year);

      this.store.notifySubscribers('syncStatus', {
        type: 'success',
        message: `Activity data for ${year} updated successfully`,
        timestamp: new Date(),
        details: {
          year,
          updatedAt: new Date().toISOString(),
          source: 'websocket'
        }
      });
    } catch (error) {
      console.error('Failed to reload activity data:', error);
      this.store.handleError('activityData', {
        message: error.message,
        details: {
          year,
          timestamp: new Date().toISOString(),
          source: 'websocket',
          originalError: error
        }
      });
    }
  }
}